/**
 * LicenseBlock — Bloque une section UI si aucune licence n'est active.
 * Le superadmin bypass automatiquement.
 *
 * Usages :
 *   // Wrapper section entière
 *   <LicenseBlock feature="les scans">
 *     <button>Nouveau scan</button>
 *   </LicenseBlock>
 *
 *   // Bouton individuel avec tooltip
 *   <LicenseGatedButton feature="la génération de rapports" onClick={...} className="...">
 *     Générer le rapport
 *   </LicenseGatedButton>
 */

import React from 'react';
import { Lock, Key } from 'lucide-react';
import { useLicense } from '../hooks/useLicense';

// ─── Wrapper section ────────────────────────────────────────────────────────
const LicenseBlock = ({ children, feature = 'cette fonctionnalité', className = '' }) => {
  const { isBlocked, loading } = useLicense();

  // Pendant le chargement du profil, ne pas flasher le blocage
  if (loading || !isBlocked) return <>{children}</>;

  return (
    <div className={`relative ${className}`}>
      {/* Contenu original flou + non interactif */}
      <div className="pointer-events-none opacity-20 select-none blur-[1px]">
        {children}
      </div>

      {/* Overlay de blocage */}
      <div className="absolute inset-0 flex items-center justify-center rounded-xl z-10">
        <div className="flex flex-col items-center text-center px-6 py-5 bg-brand-black/90 border border-brand-green/30 rounded-xl shadow-xl backdrop-blur-sm max-w-xs">
          <div className="bg-brand-green/10 border border-brand-green/30 p-3 rounded-full mb-3">
            <Lock className="w-6 h-6 text-brand-green" />
          </div>
          <p className="text-white font-semibold text-sm mb-1">Licence requise</p>
          <p className="text-gray-400 text-xs mb-4 leading-relaxed">
            Activez une licence SYLINK pour accéder à {feature}.
          </p>
          <a
            href="/settings?tab=license"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green text-black text-xs font-semibold rounded-lg hover:bg-brand-green/80 transition-colors"
          >
            <Key className="w-3.5 h-3.5" />
            Activer ma licence
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── Bouton individuel ───────────────────────────────────────────────────────
export const LicenseGatedButton = ({
  children,
  feature = 'cette fonctionnalité',
  className = '',
  onClick,
  disabled: externalDisabled,
  ...props
}) => {
  const { isBlocked } = useLicense();
  const isDisabled = isBlocked || externalDisabled;

  return (
    <div className="relative group inline-flex">
      <button
        {...props}
        onClick={isBlocked ? undefined : onClick}
        disabled={isDisabled}
        className={`${className} ${isBlocked ? 'opacity-40 cursor-not-allowed' : ''}`}
      >
        {isBlocked && <Lock className="w-3.5 h-3.5 mr-1.5 inline flex-shrink-0" />}
        {children}
      </button>
      {isBlocked && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 border border-gray-700 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          Licence requise pour {feature}
          <a href="/settings?tab=license" className="ml-2 text-brand-green underline pointer-events-auto">
            Activer
          </a>
        </div>
      )}
    </div>
  );
};

export default LicenseBlock;
